import type { UserMetric } from '../services/api';
import { normalizeInputDigits } from '../utils/localizedInput';

export interface MetricFormEntry {
  metric: UserMetric;
  value: string;
  selectedItemId: number | '';
  userMetricDataId?: number | null;
}

/** Map API metric row → form field state (handles select + numeric values). */
export function buildMetricFormEntries(metrics: UserMetric[]): MetricFormEntry[] {
  return metrics.map((metric) => {
    const items = metric.metricTypeItems ?? metric.metric_type_items ?? [];
    const parsed = parseStoredMetricValue(metric.value, items);
    return {
      metric,
      value: parsed.value,
      selectedItemId: parsed.selectedItemId,
      userMetricDataId: metric.user_metric_data_id ?? null,
    };
  });
}

function parseStoredMetricValue(
  raw: string | number | null | undefined,
  items: { id: number; en_name?: string; fa_name?: string }[],
): { value: string; selectedItemId: number | '' } {
  if (raw == null || raw === '') {
    return { value: '', selectedItemId: '' };
  }

  const str = String(raw).trim();
  if (items.length === 0) {
    return { value: str, selectedItemId: '' };
  }

  let candidateId: number | null = null;
  try {
    const parsed = JSON.parse(str);
    candidateId = typeof parsed === 'number' ? parsed : Number(parsed);
  } catch {
    candidateId = Number(str);
  }

  if (candidateId != null && !Number.isNaN(candidateId) && items.some((i) => i.id === candidateId)) {
    return { value: String(candidateId), selectedItemId: candidateId };
  }

  const byName = items.find((i) => i.en_name === str || i.fa_name === str);
  if (byName) {
    return { value: String(byName.id), selectedItemId: byName.id };
  }

  return { value: str, selectedItemId: '' };
}

/** Format value for PATCH — mirrors Ai-Tempo Metric screen. */
export function formatMetricSubmitValue(entry: MetricFormEntry): string {
  const dataType = entry.metric.data_type;
  if (dataType === 'text') {
    return entry.value;
  }
  return normalizeMetricNumber(entry.value) ?? entry.value.trim();
}

export function normalizeMetricNumber(raw: string): string | null {
  const value = normalizeInputDigits(raw).trim().replace(/[٫,]/g, '.');
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : null;
}

export function isMetricEntryValid(entry: MetricFormEntry): boolean {
  const items = entry.metric.metricTypeItems ?? entry.metric.metric_type_items ?? [];
  if (items.length) return items.some(item => item.id === Number(entry.value));
  return entry.metric.data_type === 'text' || normalizeMetricNumber(entry.value) !== null;
}
